import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { 
  RefreshCw, 
  CheckCircle, 
  ArrowDownLeft, 
  XCircle, 
  Key, 
  Landmark, 
  Wallet, 
  UserPlus,
  Bell,
  Settings,
  Smartphone,
  MessageSquare,
  Mail,
  X,
  Save,
  Send
} from 'lucide-react';
import './NotificationCenter.css';
import apiClient from '../services/apiClient';

const EVENT_TYPES = [
  {
    id: 'transfer_initiated',
    name: 'Transfer Initiated',
    description: 'When a new transfer is started (GUI or API)',
    icon: RefreshCw,
  },
  {
    id: 'transfer_completed',
    name: 'Transfer Completed',
    description: 'When a transfer successfully completes with new balance',
    icon: CheckCircle,
  },
  {
    id: 'transfer_received',
    name: 'Transfer Received',
    description: 'When funds are received at destination with new balance',
    icon: ArrowDownLeft,
  },
  {
    id: 'transfer_failed',
    name: 'Transfer Failed',
    description: 'When a transfer fails or encounters an error',
    icon: XCircle,
  },
  {
    id: 'api_key_added',
    name: 'API Key Added',
    description: 'When a new API key is created',
    icon: Key,
  },
  {
    id: 'exchange_added',
    name: 'Exchange Added',
    description: 'When a new exchange is configured',
    icon: Landmark,
  },
  {
    id: 'wallet_created',
    name: 'Wallet Created',
    description: 'When a new treasury wallet is created',
    icon: Wallet,
  },
  {
    id: 'team_member_added',
    name: 'Team Member Added',
    description: 'When a new team member is added to the account',
    icon: UserPlus,
  },
];

function NotificationCenter() {
  const [preferences, setPreferences] = useState({});
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [activeConfigTab, setActiveConfigTab] = useState('telegram');
  const [testForm, setTestForm] = useState({
    channel: 'telegram',
    recipient: '',
  });
  const [configForm, setConfigForm] = useState({
    telegram: '',
    telegramChatId: '',
    slack: '',
    slackChannel: '#alerts',
  });

  useEffect(() => {
    loadPreferences();
    loadConfig();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await apiClient.get('/notifications/preferences');
      const prefsMap = {};
      response.data.preferences.forEach((pref) => {
        prefsMap[pref.eventType] = pref;
      });
      setPreferences(prefsMap);
    } catch (error) {
      console.error('Failed to load preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const response = await apiClient.get('/notifications/config');
      console.log('[NotificationCenter] Config received:', JSON.stringify(response.data, null, 2));
      console.log('[NotificationCenter] Telegram object:', JSON.stringify(response.data?.telegram));
      console.log('[NotificationCenter] Slack object:', JSON.stringify(response.data?.slack));
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to load config:', error);
    }
  };

  const savePreference = async (eventType, data) => {
    setSaving(eventType);
    try {
      await apiClient.post('/notifications/preferences', {
        eventType,
        ...data,
      });
      await loadPreferences();
      setTestResult({ type: 'success', message: 'Notification settings saved!' });
      setTimeout(() => setTestResult(null), 3000);
    } catch (error) {
      console.error('Failed to save preference:', error);
      setTestResult({ type: 'error', message: 'Failed to save settings' });
    } finally {
      setSaving(null);
    }
  };

  const toggleEnabled = async (eventType) => {
    const defaultChannels = ['telegram', 'slack'];
    const current = preferences[eventType];
    const newEnabled = !current?.enabled;

    await savePreference(eventType, {
      enabled: newEnabled,
      channels: current?.channels?.length ? current.channels : defaultChannels,
      recipients: current?.recipients || {},
    });
  };

  const updateChannels = async (eventType, channel, enabled) => {
    // Default to both channels if no preference exists
    const defaultChannels = ['telegram', 'slack'];
    const current = preferences[eventType] || { enabled: false, channels: defaultChannels, recipients: {} };
    let newChannels = [...(current.channels?.length ? current.channels : defaultChannels)];

    if (enabled) {
      if (!newChannels.includes(channel)) {
        newChannels.push(channel);
      }
    } else {
      newChannels = newChannels.filter((c) => c !== channel);
    }

    await savePreference(eventType, {
      enabled: current.enabled,
      channels: newChannels,
      recipients: current.recipients,
    });
  };

  const updateRecipients = async (eventType, recipientType, value) => {
    const current = preferences[eventType] || { enabled: false, channels: [], recipients: {} };
    const newRecipients = { ...current.recipients };

    // Parse comma-separated values
    const values = value.split(',').map((v) => v.trim()).filter((v) => v);

    switch (recipientType) {
      case 'emails':
        newRecipients.emails = values;
        break;
      case 'telegram':
        newRecipients.telegram_chat_ids = values;
        break;
      case 'slack':
        newRecipients.slack_channels = values;
        break;
      default:
        break;
    }

    await savePreference(eventType, {
      enabled: current.enabled,
      channels: current.channels,
      recipients: newRecipients,
    });
  };

  const sendTestNotification = async () => {
    try {
      await apiClient.post('/notifications/test', testForm);
      setTestResult({ type: 'success', message: 'Test notification sent!' });
      setShowTestModal(false);
      setTestForm({ channel: 'telegram', recipient: '' });
    } catch (error) {
      console.error('Failed to send test notification:', error);
      setTestResult({ type: 'error', message: 'Failed to send test notification' });
    }
  };

  const saveConfiguration = async () => {
    try {
      const updates = [];
      
      if (configForm.telegram) {
        updates.push(
          apiClient.put('/admin/secrets/TELEGRAM_BOT_TOKEN', {
            value: configForm.telegram,
          })
        );
      }
      
      if (configForm.telegramChatId) {
        updates.push(
          apiClient.put('/admin/secrets/TELEGRAM_CHAT_ID', {
            value: configForm.telegramChatId,
          })
        );
      }
      
      if (configForm.slack) {
        updates.push(
          apiClient.put('/admin/secrets/SLACK_WEBHOOK_URL', {
            value: configForm.slack,
          })
        );
      }
      
      if (configForm.slackChannel) {
        updates.push(
          apiClient.put('/admin/secrets/SLACK_CHANNEL', {
            value: configForm.slackChannel,
          })
        );
      }

      if (updates.length === 0) {
        setTestResult({ type: 'warning', message: 'No configuration to save' });
        return;
      }

      await Promise.all(updates);
      
      setTestResult({ type: 'success', message: 'Configuration saved successfully!' });
      setShowConfigModal(false);
      setConfigForm({ telegram: '', telegramChatId: '', slack: '', slackChannel: '#alerts' });
      
      // Reload config to reflect changes
      await loadConfig();
      
      setTimeout(() => setTestResult(null), 3000);
    } catch (error) {
      console.error('Failed to save configuration:', error);
      setTestResult({ 
        type: 'error', 
        message: error.response?.data?.error || 'Failed to save configuration' 
      });
    }
  };

  if (loading) {
    return (
      <div className="notification-center">
        <h2>Notification Center</h2>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="notification-center">
      <div className="notification-header">
        <div>
          <h2>Notification Center</h2>
          <p className="notification-subtitle">
            Configure notifications for important events. Get notified via Telegram, Slack, or Email.
          </p>
        </div>
      </div>

      {testResult && (
        <div className={`notification-alert ${testResult.type}`}>
          {testResult.message}
        </div>
      )}

      {/* Channels Configuration Section */}
      <div className="notification-channels-section">
        <h3>Notification Channels</h3>
        <div className="channels-grid">
          {/* Telegram Card */}
          <div className={`channel-card ${config?.telegram.configured ? 'configured' : ''}`}>
            <div className="channel-header">
              <div className="channel-title">
                <div className="channel-icon telegram">
                  <Smartphone size={24} />
                </div>
                <div>
                  <h4>Telegram</h4>
                  <span className={`status-badge ${config?.telegram.configured ? 'active' : ''}`}>
                    {config?.telegram.configured ? 'Configured' : 'Not Configured'}
                  </span>
                </div>
              </div>
              <div className="channel-actions">
                {config?.telegram.configured && (
                  <button 
                    className="btn-icon-only" 
                    title="Test Telegram"
                    onClick={() => {
                      setTestForm({ ...testForm, channel: 'telegram' });
                      setShowTestModal(true);
                    }}
                  >
                    <Send size={18} />
                  </button>
                )}
                <button 
                  className="btn-icon-only"
                  title="Configure Telegram"
                  onClick={() => {
                    setActiveConfigTab('telegram');
                    // Populate form with saved values when opening modal
                    if (config?.telegram) {
                      setConfigForm({
                        telegram: config.telegram.botTokenPreview || '',
                        telegramChatId: config.telegram.chatIdPreview || '',
                        slack: configForm.slack,
                        slackChannel: configForm.slackChannel,
                      });
                    }
                    setShowConfigModal(true);
                  }}
                >
                  <Settings size={18} />
                </button>
              </div>
            </div>
          </div>

          {/* Slack Card */}
          <div className={`channel-card ${config?.slack.configured ? 'configured' : ''}`}>
            <div className="channel-header">
              <div className="channel-title">
                <div className="channel-icon slack">
                  <MessageSquare size={24} />
                </div>
                <div>
                  <h4>Slack</h4>
                  <span className={`status-badge ${config?.slack.configured ? 'active' : ''}`}>
                    {config?.slack.configured ? 'Configured' : 'Not Configured'}
                  </span>
                </div>
              </div>
              <div className="channel-actions">
                {config?.slack.configured && (
                  <button 
                    className="btn-icon-only" 
                    title="Test Slack"
                    onClick={() => {
                      setTestForm({ ...testForm, channel: 'slack' });
                      setShowTestModal(true);
                    }}
                  >
                    <Send size={18} />
                  </button>
                )}
                <button 
                  className="btn-icon-only"
                  title="Configure Slack"
                  onClick={() => {
                    setActiveConfigTab('slack');
                    // Populate form with saved values when opening modal
                    if (config?.slack) {
                      setConfigForm({
                        telegram: configForm.telegram,
                        telegramChatId: configForm.telegramChatId,
                        slack: config.slack.webhookPreview || '',
                        slackChannel: config.slack.channelPreview || '#alerts',
                      });
                    }
                    setShowConfigModal(true);
                  }}
                >
                  <Settings size={18} />
                </button>
              </div>
            </div>
          </div>

          {/* Email Card */}
          <div className={`channel-card ${config?.email.configured ? 'configured' : ''}`}>
            <div className="channel-header">
              <div className="channel-title">
                <div className="channel-icon email">
                  <Mail size={24} />
                </div>
                <div>
                  <h4>Email</h4>
                  <span className={`status-badge ${config?.email.configured ? 'active' : ''}`}>
                    {config?.email.configured ? 'Configured' : 'System Only'}
                  </span>
                </div>
              </div>
              <div className="channel-actions">
                {config?.email.configured && (
                  <button 
                    className="btn-icon-only" 
                    title="Test Email"
                    onClick={() => {
                      setTestForm({ ...testForm, channel: 'email' });
                      setShowTestModal(true);
                    }}
                  >
                    <Send size={18} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="notification-events">
        <h3>Event Notifications</h3>
        {EVENT_TYPES.map((event) => {
          const pref = preferences[event.id];
          const isEnabled = pref?.enabled || false;
          // Default to both channels selected if no preference exists
          const channels = pref?.channels || ['telegram', 'slack'];
          const Icon = event.icon;

          return (
            <div key={event.id} className="event-card">
              <div className="event-info">
                <Icon size={14} />
                <span className="event-name">{event.name}</span>
              </div>
              <div className="channel-toggles">
                {config?.telegram.configured && (
                  <label className="channel-toggle">
                    <input
                      type="checkbox"
                      checked={channels.includes('telegram')}
                      onChange={(e) => updateChannels(event.id, 'telegram', e.target.checked)}
                    />
                    <span>TG</span>
                  </label>
                )}
                {config?.slack.configured && (
                  <label className="channel-toggle">
                    <input
                      type="checkbox"
                      checked={channels.includes('slack')}
                      onChange={(e) => updateChannels(event.id, 'slack', e.target.checked)}
                    />
                    <span>Slack</span>
                  </label>
                )}
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={isEnabled}
                  onChange={() => toggleEnabled(event.id)}
                  disabled={saving === event.id}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          );
        })}
      </div>

      {showTestModal && (
        <div className="modal-overlay" onClick={() => setShowTestModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Send Test Notification</h3>
            <div className="form-group">
              <label>Channel</label>
              <select
                value={testForm.channel}
                onChange={(e) => setTestForm({ ...testForm, channel: e.target.value })}
              >
                <option value="telegram">Telegram</option>
                <option value="slack">Slack</option>
                <option value="email">Email</option>
              </select>
            </div>
            <div className="form-group">
              <label>
                {testForm.channel === 'telegram'
                  ? 'Chat ID'
                  : testForm.channel === 'slack'
                  ? 'Channel'
                  : 'Email'}
              </label>
              <input
                type="text"
                value={testForm.recipient}
                onChange={(e) => setTestForm({ ...testForm, recipient: e.target.value })}
                placeholder={
                  testForm.channel === 'telegram'
                    ? '123456789'
                    : testForm.channel === 'slack'
                    ? '#general'
                    : 'user@example.com'
                }
              />
            </div>
            <div className="modal-actions">
              <button className="btn-secondary-outline" onClick={() => setShowTestModal(false)}>
                Cancel
              </button>
              <button className="btn-primary-outline" onClick={sendTestNotification}>
                <Send size={16} /> Send Test
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfigModal && (
        <div className="modal-overlay" onClick={() => setShowConfigModal(false)}>
          <div className="modal-content config-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Configure Notification Channels</h3>
              <button className="btn-icon-only" onClick={() => setShowConfigModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="config-tabs">
              <button
                className={activeConfigTab === 'telegram' ? 'active' : ''}
                onClick={() => {
                  setActiveConfigTab('telegram');
                  // Populate form with saved values when opening telegram tab
                  if (config?.telegram) {
                    setConfigForm({
                      ...configForm,
                      telegram: config.telegram.botTokenPreview || '',
                      telegramChatId: config.telegram.chatIdPreview || '',
                    });
                  }
                }}
              >
                <Smartphone size={16} /> Telegram
              </button>
              <button
                className={activeConfigTab === 'slack' ? 'active' : ''}
                onClick={() => {
                  setActiveConfigTab('slack');
                  // Populate form with saved values when opening slack tab
                  if (config?.slack) {
                    setConfigForm({
                      ...configForm,
                      slack: config.slack.webhookPreview || '',
                      slackChannel: config.slack.channelPreview || '#alerts',
                    });
                  }
                }}
              >
                <MessageSquare size={16} /> Slack
              </button>
            </div>

            {activeConfigTab === 'telegram' && (
              <div className="config-tab-content">
                <div className="instructions">
                  <h4>How to Setup Telegram Bot</h4>
                  <ReactMarkdown>
{`1. Message [@BotFather](https://t.me/BotFather) → send \`/newbot\` → copy the token
2. **For personal notifications:** Message [@userinfobot](https://t.me/userinfobot) to get your Chat ID (positive number)
3. **For group notifications:** Add your bot to the group, then use [@getidsbot](https://t.me/getidsbot) to get the group Chat ID (negative number)
4. Search for your bot and send \`/start\` (personal) or add it to your group`}
                  </ReactMarkdown>
                </div>
                <div className="form-group">
                  <label>Bot Token</label>
                  <input
                    type="text"
                    value={configForm.telegram || config?.telegram?.botTokenPreview || ''}
                    onChange={(e) => setConfigForm({ ...configForm, telegram: e.target.value })}
                    placeholder={config?.telegram?.botTokenPreview || "123456789:ABCdefGHIjklMNOpqrsTUVwxyz"}
                  />
                  {config?.telegram?.botTokenPreview && !configForm.telegram && (
                    <small style={{ color: '#888', fontStyle: 'italic' }}>
                      Current: {config.telegram.botTokenPreview} (masked)
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Default Chat IDs (Comma-separated)</label>
                  <textarea
                    value={configForm.telegramChatId || config?.telegram?.chatIdPreview || ''}
                    onChange={(e) => setConfigForm({ ...configForm, telegramChatId: e.target.value })}
                    placeholder={config?.telegram?.chatIdPreview || "1388949847, -1001234567890"}
                    rows={3}
                    style={{ fontFamily: 'monospace', fontSize: '13px' }}
                  />
                  {config?.telegram?.chatIdPreview && !configForm.telegramChatId && (
                    <small style={{ color: '#888', fontStyle: 'italic' }}>
                      Current: {config.telegram.chatIdPreview}
                    </small>
                  )}
                  <small>
                    <strong>Multiple recipients:</strong> Separate with commas (e.g., <code>1388949847, -1001234567890</code>)<br/>
                    • <strong>Personal:</strong> Get from <a href="https://t.me/userinfobot" target="_blank" rel="noopener noreferrer">@userinfobot</a> (positive number)<br/>
                    • <strong>Group:</strong> Add bot to group, then use <a href="https://t.me/getidsbot" target="_blank" rel="noopener noreferrer">@getidsbot</a> (negative number like <code>-1001234567890</code>)
                  </small>
                </div>
              </div>
            )}

            {activeConfigTab === 'slack' && (
              <div className="config-tab-content">
                <div className="instructions">
                  <h4>How to Setup Slack Webhook</h4>
                  <ReactMarkdown>
{`1. Go to [Slack Apps](https://api.slack.com/apps) → Create New App
2. Enable **Incoming Webhooks** → Add to channel
3. Copy the Webhook URL`}
                  </ReactMarkdown>
                </div>
                <div className="form-group">
                  <label>Webhook URL</label>
                  <input
                    type="text"
                    value={configForm.slack || config?.slack?.webhookPreview || ''}
                    onChange={(e) => setConfigForm({ ...configForm, slack: e.target.value })}
                    placeholder={config?.slack?.webhookPreview || "https://hooks.slack.com/services/..."}
                  />
                  {config?.slack?.webhookPreview && !configForm.slack && (
                    <small style={{ color: '#888', fontStyle: 'italic' }}>
                      Current: {config.slack.webhookPreview} (masked)
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Default Channel</label>
                  <input
                    type="text"
                    value={configForm.slackChannel || config?.slack?.channelPreview || ''}
                    onChange={(e) => setConfigForm({ ...configForm, slackChannel: e.target.value })}
                    placeholder={config?.slack?.channelPreview || "#alerts"}
                  />
                  {config?.slack?.channelPreview && !configForm.slackChannel && (
                    <small style={{ color: '#888', fontStyle: 'italic' }}>
                      Current: {config.slack.channelPreview}
                    </small>
                  )}
                  <small>The channel selected when creating the webhook</small>
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button 
                className="btn-secondary-outline" 
                onClick={() => {
                  setShowConfigModal(false);
                  setConfigForm({ telegram: '', telegramChatId: '', slack: '', slackChannel: '#alerts' });
                }}
              >
                Cancel
              </button>
              <button 
                className="btn-primary-outline" 
                onClick={saveConfiguration}
              >
                <Save size={16} /> Save Configuration
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationCenter;
